import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CheckCircle, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, today } from "@/lib/api";

export const MarkPaidDialog = ({ invoice, onClose, onDone }) => {
  const [paidDate, setPaidDate] = useState(today());
  const [busy, setBusy] = useState("");
  if (!invoice) return null;
  const hasEmail = !!invoice.client?.email;

  const go = async (sendReceipt) => {
    setBusy(sendReceipt ? "receipt" : "paid");
    try {
      const { data } = await api.patch(`/invoices/${invoice.id}/status`, { status: "paid", paid_date: paidDate, send_receipt: sendReceipt });
      if (data.receipt_error) toast.warning(`Marked paid, but receipt email failed: ${data.receipt_error}`, { duration: 9000 });
      else toast.success(sendReceipt ? `Marked paid · receipt emailed to ${invoice.client.email}` : "Marked paid");
      onDone(data);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="mark-paid-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><CheckCircle className="h-5 w-5 text-emerald-600" /> Mark {invoice.invoice_number} paid</DialogTitle>
          <DialogDescription>{invoice.client?.name} · {money(invoice.total)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Payment date</Label>
            <Input data-testid="mark-paid-date-input" type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} />
          </div>
          <Button data-testid="mark-paid-send-receipt-button" disabled={!!busy || !hasEmail} onClick={() => go(true)} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            <MailCheck className="h-4 w-4 mr-2" /> {busy === "receipt" ? "Sending..." : "Mark paid & email receipt"}
          </Button>
          <Button data-testid="mark-paid-only-button" variant="outline" disabled={!!busy} onClick={() => go(false)} className="w-full h-11 font-semibold">
            {busy === "paid" ? "Saving..." : "Mark paid only"}
          </Button>
          {!hasEmail && <p className="text-xs text-amber-600 font-medium">This client has no email on file, so a receipt can't be emailed.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
